<template>

  <div id="overlay" />
  <div id="tooltips" />

  <component :is="$route.meta.layout || 'div'">
    <router-view />
  </component>

  <div v-if="deployEnv === 'demo'" id="demoMode" class="state-pill danger top-level-toast">
    {{ $t('global.demoMode') }}
  </div>

  <div v-if="isImpersonating" id="impersonateToast" class="state-pill danger top-level-toast">
    {{ $t('global.impersonating', [$store.state.auth.user?.firstName, $store.state.auth.user?.lastName]) }}
    <a href="#" class="underline" @click.prevent="stopImpersonating">
      {{ $t('global.stopImpersonating') }}
    </a>
  </div>

  <div
    v-if="showDevDialog"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
    @click.self="showDevDialog = false"
  >
    <div class="bg-white rounded-lg p-6 w-80 shadow-xl">
      <h3 class="text-lg font-semibold mb-4">Enter Developer Passphrase</h3>
      <input
        ref="devCodeInput"
        v-model="devCode"
        type="password"
        class="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:border-green"
        placeholder="Developer passphrase"
        @keydown.enter="submitDevCode"
        @keydown.esc="showDevDialog = false"
      >
      <div class="flex justify-end gap-2">
        <button class="px-4 py-2 text-gray-600" @click="showDevDialog = false">
          Cancel
        </button>
        <button class="px-4 py-2 bg-green text-white rounded" @click="submitDevCode">
          Submit
        </button>
      </div>
    </div>
  </div>

</template>

<script>
import {
  computed, defineAsyncComponent, onMounted, onUnmounted, provide, ref, nextTick, watch,
} from 'vue';
import { useHead } from '@vueuse/head';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import EventEmitter from 'events';
import { useStore } from 'vuex';
import { useNetworkObserver } from './hooks/useNetworkObserver';
import axios from './services/axios';
import { setAuthToken } from './utils/TokenManager';

export default {
  components: {
    Dashboard: defineAsyncComponent(() => import('@/components/layout/Dashboard.vue')),
    Public: defineAsyncComponent(() => import('@/components/layout/Public.vue')),
    TaskLayout: defineAsyncComponent(() => import('@/components/layout/TaskLayout.vue')),
    OnboardingLayout: defineAsyncComponent(() => import('@/components/layout/OnboardingLayout.vue')),
  },
  setup() {
    useNetworkObserver();

    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const { locale, t } = useI18n();

    const eventEmitter = new EventEmitter();
    provide('eventEmitter', eventEmitter);

    const deployEnv = computed(() => window.env?.VITE_DEPLOY_ENV || import.meta.env.VITE_DEPLOY_ENV);
    const isImpersonating = computed(() => {
      if (localStorage != null) {
        return typeof localStorage.getItem('_switch_user') === 'string';
      }
      return false;
    });
    const stopImpersonating = async () => {
      localStorage.removeItem('_switch_user');
      const link = document.createElement('a');
      link.href = '/admin';
      link.click();
    };

    const showDevDialog = ref(false);
    const devCode = ref('');
    const devCodeInput = ref(null);

    const isSuperAdmin = computed(() => store.state.auth.user?.roles?.includes('ROLE_SUPER_ADMIN') ?? false);

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isSuperAdmin.value) {
          showDevDialog.value = true;
          devCode.value = '';
          nextTick(() => {
            devCodeInput.value?.focus();
          });
        }
      }
    };

    const submitDevCode = async () => {
      try {
        const { data } = await axios.post('/users/toggle-developer-mode', { code: devCode.value });
        setAuthToken(data.token);
        await store.dispatch('auth/LOAD_USER');
        toast.info(t(data.enabled ? 'global.developerModeActivated' : 'global.developerModeDeactivated'));
        showDevDialog.value = false;

        if (!data.enabled) {
          router.replace('/admin');
        }
      } catch (err) {
        toast.error('Invalid code');
      }
      devCode.value = '';
    };

    onMounted(async () => {
      if (store.state.auth.user?.locale) {
        locale.value = store.state.auth.user.locale;
      }
      window.addEventListener('keydown', handleKeyDown);
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeyDown);
    });

    useHead({
      title: computed(() => `${route.meta.title ? `${t(route.meta.title)} - ` : ''}COMPLYANT`),
    });

    return {
      deployEnv,
      isImpersonating,
      stopImpersonating,
      showDevDialog,
      devCode,
      devCodeInput,
      submitDevCode,
    };
  },
};
</script>

<style lang="scss" scoped>
.top-level-toast {
    @apply fixed top-4 w-96 text-center left-1/2 transform -translate-x-1/2 z-[150];
  }
</style>
